import { cloneDeep, uniqBy } from "lodash";
import {
  ALL_PNL_CATEGORIES,
  LOSS_CATEGORIES_STRINGS,
  PROFIT_CATEGORIES_STRINGS,
  ALL_PNL_CATEGORIES_STRINGS,
} from "../constants/constants";
import type {
  PnlCategoryObjOfObjectsOfNumbers,
  ProductionSegmentType,
  ProductionAggregate,
  Process,
  PnlsOrTagsWithMeasurementObj,
  TagSummary,
  ShiftSegment,
  RollupWithTagsAndCountsAndRange,
  RollupCategories,
  ManyRollupsWithTagsAndRange,
  DataDumpAggregateType,
} from "../types/types";
import { PnlCategoriesEnum } from "../types/enums";
import { ONE_DAY_OF_MS } from "./timeUtils";
import { isEqual } from "lodash";

export const findDataDumpsTrendChartObjFromStore = ({
  dataDumpsTrendChart,
  geToFind,
  processIdsToMatch,
}: {
  dataDumpsTrendChart?: DataDumpAggregateType[];
  geToFind: number | null;
  processIdsToMatch?: string[];
}): DataDumpAggregateType | null => {
  return (
    (dataDumpsTrendChart &&
      geToFind &&
      dataDumpsTrendChart.find(
        ({ range, processIds }) =>
          range.ge === geToFind &&
          (!processIdsToMatch ||
            isEqual([...processIds].sort(), [...processIdsToMatch].sort()))
      )) ||
    null
  );
};

// pass in a value between 0 and 1 for estimatedFutureMetricPercent,
// calculate & return the metric (TEEP, avail, uptime) by assuming
// the rest of the selectedShift is filled with
// (estimatedFutureMetricPercent * rest-of-shift-time) of producing time,
// remainder is unknown time
type getPredictedFutureMetricProps = {
  isOver24Hours: boolean;
  dataDumpKey: keyof DataDumpAggregateType;
  selectedShift: ShiftSegment | null;
  rollupDisplay: RollupWithTagsAndCountsAndRange | null;
  numProcesses: number;
  estimatedFutureMetricPercent: number;
  dataDumpsObjForFullRange?: DataDumpAggregateType | null;
  /** getNumbers: return number | undefined instead of string */
  getNumbers?: boolean;
  isMidgardDDAEnabled: boolean;
};
export const getPredictedFutureMetric = ({
  isOver24Hours,
  dataDumpKey,
  selectedShift,
  rollupDisplay,
  numProcesses,
  estimatedFutureMetricPercent,
  dataDumpsObjForFullRange,
  getNumbers = false,
  isMidgardDDAEnabled,
}: getPredictedFutureMetricProps): string | number | undefined => {
  if (!selectedShift || !rollupDisplay || !numProcesses || numProcesses === 0) {
    return getNumbers ? undefined : "--";
  }
  const shiftTime = isOver24Hours
    ? ONE_DAY_OF_MS
    : selectedShift.range.lt - selectedShift.range.ge;

  const pnlCategoriesStringsArr = ALL_PNL_CATEGORIES.map(
    ({ category }) => category
  );

  const rollupTimeSoFar = Object.entries(rollupDisplay).reduce(
    (acc: number, [key, time]: [string, any]) => {
      return isFinite(Number(time)) && pnlCategoriesStringsArr.includes(key)
        ? acc + Number(time)
        : acc;
    },
    0
  );
  const timeLeftInShift =
    // max time left = shift time
    Math.min(
      shiftTime,
      // min time left = 0
      Math.max(0, shiftTime - rollupTimeSoFar / numProcesses)
    );

  // estimate by piling the extra production onto 'producing', and the rest onto 'unknown'
  const rollupDisplayWithEstimatedProduction = {
    ...rollupDisplay,
    producing:
      rollupDisplay.producing + estimatedFutureMetricPercent * timeLeftInShift,
    unknown:
      rollupDisplay.unknown +
      (1 - estimatedFutureMetricPercent) * timeLeftInShift,
  };

  const estimatedMetric = calcMetric({
    rollupMetric: rollupDisplayWithEstimatedProduction,
    dataDumpKey,
    dataDumpsObjForRange: dataDumpsObjForFullRange,
    isMidgardDDAEnabled,
  });

  return estimatedMetric || estimatedMetric === 0
    ? getNumbers
      ? Math.round(Math.max(0, estimatedMetric))
      : Math.max(0, estimatedMetric).toFixed(0)
    : getNumbers
      ? undefined
      : "--";
};

export const initRollupCategories = (placeholder = 0 as any) => {
  const m = {};
  for (const c of Object.values(PnlCategoriesEnum)) {
    m[c] = placeholder;
  }
  return m;
};

export const initRollupAcc = (): RollupWithTagsAndCountsAndRange =>
  cloneDeep({
    ...initRollupCategories(),
    tags: cloneDeep(initRollupCategories({})),
    counts: cloneDeep(initRollupCategories({})),
  }) as RollupWithTagsAndCountsAndRange;

export const calcRollup = (
  segments: ProductionSegmentType[],
  range: { ge: number; lt: number }
): RollupWithTagsAndCountsAndRange =>
  // TECHDEBT: binary search here (bisection) to find where to start instead (jump, don't visit every segment)
  //       could pass back index for next rollup being calculated
  //       like in trend chart or addrollups
  //       could terminate early...
  //       need to ensure passing in sorted segments
  segments.reduce(
    (acc, s) => {
      // duration = max(segment start, range start) - min(segment end, range end)
      const segStart = s.range.ge ? Math.max(s.range.ge, range.ge) : range.ge;
      const segEnd = s.range.lt ? Math.min(s.range.lt, range.lt) : range.lt;
      const duration = segEnd - segStart;

      let category = s.pnl && s.pnl.category ? s.pnl.category : "unknown";
      category = category === "plantLoss" ? "notScheduled" : category;
      category = category === "running" ? "producing" : category;
      const tag = s.tag && s.tag.name ? s.tag.name : null;

      if (duration > 0) {
        return {
          ...acc,
          totalDurationMs: acc.totalDurationMs + duration,
          [category]: acc[category] ? acc[category] + duration : duration,
          tags: tag
            ? {
                ...acc.tags,
                [category]: {
                  ...acc.tags[category],
                  [tag]:
                    tag in acc.tags[category]
                      ? acc.tags[category][tag] + duration
                      : duration,
                },
              }
            : acc.tags,
          counts: tag
            ? {
                ...acc.counts,
                [category]: {
                  ...acc.counts[category],
                  [tag]:
                    tag in acc.counts[category]
                      ? acc.counts[category][tag] + 1
                      : 1,
                },
              }
            : acc.counts,
        };
      }
      return acc;
    },
    {
      ...initRollupAcc(),
      totalDurationMs: 0,
      range,
    } as RollupWithTagsAndCountsAndRange
  );

const getTagsObjFromAggregate = (
  aggregate: ProductionAggregate
): PnlCategoryObjOfObjectsOfNumbers =>
  Object.entries(aggregate)
    // currently speedLoss, speedGain, qualityLoss aren't implemented -- they default to numbers (0)
    // so we only keep objects containing tags
    .filter(
      ([category, pnlObj]) => pnlObj instanceof Object && "tags" in pnlObj
    )
    .reduce((acc, [category, pnlObj]) => {
      // initialize the tag category in the accumulator
      if (!(category in acc)) {
        acc[category] = {} as PnlsOrTagsWithMeasurementObj;
      }
      const { tags } = pnlObj as any;
      // if we already have this tag, add the time
      tags.forEach(({ tag, totalDurationMs }: TagSummary) => {
        if (tag && tag.name && tag.name in acc[category]) {
          acc[category][tag.name] += totalDurationMs;
        } else if (tag && tag.name) {
          // otherwise initialize it
          acc[category][tag.name] = totalDurationMs;
        }
      });
      return acc;
    }, {} as PnlCategoryObjOfObjectsOfNumbers);

export const mapAggregateToRollup = (
  aggregate: ProductionAggregate
): RollupWithTagsAndCountsAndRange =>
  Object.entries(aggregate).reduce(
    (acc, [category, pnlObj]) => {
      if (["__typename", "asOf", "range"].includes(category)) {
        return acc;
      } else if (pnlObj instanceof Object && "totalDurationMs" in pnlObj) {
        acc[category] = pnlObj.totalDurationMs;
      } else {
        acc[category] = pnlObj;
      }
      return acc;
    },
    {
      range: { ge: Number(aggregate.range.ge), lt: Number(aggregate.range.lt) },
      tags: getTagsObjFromAggregate(aggregate),
      counts: cloneDeep(initRollupCategories({})),
      shift: aggregate.shift || null,
    } as RollupWithTagsAndCountsAndRange
  );

export const addRollupValues = (
  rollupValues: RollupWithTagsAndCountsAndRange[],
  // if no rangeDuration is passed,
  // range is the same as the first rollup
  rangeDuration?: number
): RollupWithTagsAndCountsAndRange => {
  if (!rollupValues || Object.keys(rollupValues).length === 0) {
    return makeEmptyRollup();
  }
  let totalDurationMsAccum = 0;
  const pnlCatSumsObj = rollupValues.reduce(
    (
      acc,
      { tags, shift, range, totalDurationMs, counts, ...pnlCategories }
    ) => {
      totalDurationMsAccum += totalDurationMs;
      Object.entries(pnlCategories).forEach(([pnlCat, time]) => {
        if (ALL_PNL_CATEGORIES_STRINGS.includes(pnlCat)) {
          acc[pnlCat] = (acc[pnlCat] || 0) + time;
        }
      });

      return { ...acc, shift, range, totalDurationMs, counts, tags };
    },
    {} as RollupCategories
  );

  const pnlCatsObjArray = rollupValues.map(d => d.tags);
  const tagsReturned = pnlCatsObjArray.reduce((acc, pnlCatsObj) => {
    Object.entries(pnlCatsObj || {}).forEach(([pnlCat, tagsObj]) => {
      acc[pnlCat] = acc[pnlCat] || ({} as any);
      Object.entries(tagsObj).forEach(([tag, time]) => {
        acc[pnlCat][tag] = (acc[pnlCat][tag] || 0) + time;
      });
    });
    return acc;
  }, {} as PnlCategoryObjOfObjectsOfNumbers);

  const pnlCatsCountObjArray = rollupValues.map(d => d.counts);
  const countsReturned = pnlCatsCountObjArray.reduce((acc, pnlCatsCountObj) => {
    Object.entries(pnlCatsCountObj || {}).forEach(([pnlCat, countsObj]) => {
      acc[pnlCat] = acc[pnlCat] || ({} as any);
      // don't count producing/uncollected tags
      if (!["uncollected"].includes(pnlCat)) {
        Object.entries(countsObj).forEach(([tag, count]) => {
          acc[pnlCat][tag] = (acc[pnlCat][tag] || 0) + count;
        });
      }
    });
    return acc;
  }, {} as PnlCategoryObjOfObjectsOfNumbers);

  const firstRollupRange = rollupValues[0].range;

  return {
    ...pnlCatSumsObj,
    range: {
      ge: +firstRollupRange.ge,
      lt: rangeDuration
        ? +firstRollupRange.ge + rangeDuration
        : +firstRollupRange.lt,
    },
    totalDurationMs: totalDurationMsAccum,
    tags: tagsReturned,
    counts: countsReturned,
  };
};

const makeEmptyPnlObject = () => ({
  producing: {},
  planned: {},
  unplanned: {},
  waiting: {},
  unknown: {},
  notScheduled: {},
  uncollected: {},
});
export const makeEmptyRollup = (): RollupWithTagsAndCountsAndRange => ({
  producing: 0,
  planned: 0,
  unplanned: 0,
  waiting: 0,
  unknown: 0,
  notScheduled: 0,
  uncollected: 0,
  tags: makeEmptyPnlObject(),
  counts: makeEmptyPnlObject(),
  totalDurationMs: 0,
  range: { ge: 0, lt: 0 },
});

export const addRollups = (
  rollupsMetricByProcess: ManyRollupsWithTagsAndRange,
  processes?: Process[]
): RollupWithTagsAndCountsAndRange => {
  if (
    !rollupsMetricByProcess ||
    Object.keys(rollupsMetricByProcess).length === 0
  ) {
    return makeEmptyRollup();
  }
  const rollupsFiltered: ManyRollupsWithTagsAndRange = Object.entries(
    rollupsMetricByProcess
  ).reduce((acc, [id, rollupMetric]) => {
    if (!processes) {
      // if no processes passed in, add all the rollups
      acc = { ...acc, [id]: rollupMetric };
      return acc;
    }
    // if processes passed in, filter out other processes
    const processIds: string[] = uniqBy(processes, "id").map(p => p.id);
    if (processIds.includes(id)) {
      acc = { ...acc, [id]: rollupMetric };
    }
    return acc;
  }, {} as ManyRollupsWithTagsAndRange);
  const rollupValues = Object.values(rollupsFiltered);

  return addRollupValues(rollupValues);
};

export const calcTeepPercent = (rollup: RollupCategories) => {
  const {
    producing,
    planned,
    unplanned,
    waiting,
    unknown,
    notScheduled, // uncollected,
  } = rollup;
  const plantTime =
    (producing ? producing : 0) +
    (planned ? planned : 0) +
    (unplanned ? unplanned : 0) +
    (waiting ? waiting : 0) +
    (unknown ? unknown : 0) +
    (notScheduled ? notScheduled : 0);
  return plantTime ? (100 * (producing || 0)) / plantTime : undefined;
};

const calcAvailPercent = (rollup: RollupCategories) => {
  const { producing, planned, unplanned, waiting, unknown } = rollup;
  const productionTime =
    (producing ? producing : 0) +
    (planned ? planned : 0) +
    (unplanned ? unplanned : 0) +
    (waiting ? waiting : 0) +
    (unknown ? unknown : 0);
  return productionTime ? (100 * (producing || 0)) / productionTime : undefined;
};

const calcUptimePercent = (rollup: RollupCategories) => {
  const { producing, unplanned, waiting, unknown } = rollup;
  const denom =
    (producing ? producing : 0) +
    (unplanned ? unplanned : 0) +
    (waiting ? waiting : 0) +
    (unknown ? unknown : 0);
  return denom ? (100 * (producing || 0)) / denom : undefined;
};

export const getMaxTotalButterflyWingTimeMs = ({
  rollupDisplay,
}: {
  rollupDisplay: RollupWithTagsAndCountsAndRange;
}) => {
  const isNoDataInRollup =
    Object.values(rollupDisplay).reduce(
      (tally, cur) =>
        typeof cur === "number" ? (tally as number) + cur : tally,
      0
    ) === 0;
  if (isNoDataInRollup) {
    return 0;
  }

  // Left and right stack totals are used to keep the left and
  // right at the same scale, while having the larger full-width
  let [leftCategoriesTotal, rightCategoriesTotal] = [0, 0];

  Object.entries(rollupDisplay).forEach(([key, val]) => {
    if (LOSS_CATEGORIES_STRINGS.includes(key)) {
      leftCategoriesTotal += +(val as number);
    } else if (PROFIT_CATEGORIES_STRINGS.includes(key)) {
      rightCategoriesTotal += +(val as number);
    }
  });

  return Math.max(leftCategoriesTotal, rightCategoriesTotal);
};

// tslint:disable-next-line: cognitive-complexity
export function calcMetric({
  rollupMetric,
  dataDumpKey,
  dataDumpsObjForRange,
  isMidgardDDAEnabled,
}: {
  rollupMetric?: RollupWithTagsAndCountsAndRange | null;
  dataDumpKey: keyof DataDumpAggregateType;
  dataDumpsObjForRange?: DataDumpAggregateType | null;
  isMidgardDDAEnabled: boolean;
}) {
  const value = dataDumpsObjForRange?.[dataDumpKey];
  switch (dataDumpKey) {
    case "oeePercent":
    case "effectiveUptimePercent":
    case "uslePercent":
    case "netOutput":
    case "unplannedPercent":
    case "operationalAvailabilityPercent":
    case "taggingPercent":
    case "performancePercent":
    case "efficiencyPercent":
    case "qualityPercent":
    case "rejectOutput":
    case "totalPotentialOutput":
      return value != null // 0 is acceptable
        ? Math.max(0, Number(value))
        : undefined; // undefined will display "--" instead of the metric, see SingleGroupMetricValue
    // for uptime / teep / avail, only customers who use the Pnl Losses data dump should
    // see metrics from the data dump (since their charts also show this data)

    // OEE customers use the Midgard dataDumpAggregate, and *don't* fall back on Askr data

    // Flight supports the following metrics calculated from Askr data via calcAskrMetric:
    case "uptimePercent":
    case "availabilityPercent":
    case "teepPercent":
      return isMidgardDDAEnabled
        ? value != null /* 0 is acceptable */
          ? Math.max(0, Number(value))
          : undefined
        : rollupMetric
          ? calcAskrMetric(dataDumpKey, rollupMetric)
          : undefined;

    // TODO: remove this?
    // Assuming there is a better way of formatting these as times that is based on the
    // metric's unit
    case "meanTimeBetweenFailures":
    case "meanTimeToRecovery":
    case "avgCycleTime":
      return value != null
        ? Number((Math.max(0, Number(value)) / 1000 / 60).toFixed(1))
        : undefined;

    default:
      // assume it's a dataDump key
      return value != null // 0 is acceptable
        ? Math.max(0, Number(value))
        : undefined; // undefined will display "--" instead of the metric, see SingleGroupMetricValue
  }
}

const calcAskrMetric = (
  dataDumpKey: keyof DataDumpAggregateType,
  rollupMetric: RollupWithTagsAndCountsAndRange
) => {
  switch (dataDumpKey) {
    case "uptimePercent":
      return calcUptimePercent(rollupMetric);
    case "availabilityPercent":
      return calcAvailPercent(rollupMetric);
    case "teepPercent":
      return calcTeepPercent(rollupMetric);
    default:
      return undefined;
  }
};
